/**
 * Implémentation mock de la subvention culture.
 *
 * POINT D'INTÉGRATION BACKEND
 * Remplacer ce fichier par des appels HTTP (JSON:API / REST) en conservant
 * l'interface `CultureSubsidyApi`. Les vues et le store n'ont pas besoin
 * d'être réécrits : seul ce module change.
 */
import { CultureSubsidyApi } from './types'
import {
  CatalogProduct,
  CultureSubsidySettings,
  CultureSubsidySnapshot,
  OperationPayload,
  PocketBalances,
  PocketKind,
  ReserveMovement,
  SettingsPayload,
  SimulateInput,
  SpecialOperation,
  SubsidyAlert,
  SubsidyComputation,
  SupervisedCaretaker,
  TransferPayload
} from '../types'
import { buildRuntimeAlerts, createSeedDatabase, MockDatabase, toSupervisedCaretaker } from '../data/seed'
import { computeSubsidy, findOperationConflicts, roundMoney } from '../utils/calculator'

const STORAGE_KEY = 'walleo_culture_subsidy_mock_v2'
const LATENCY_MS = 280

function delay(ms = LATENCY_MS): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, ms))
}

function uid(prefix: string): string {
  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`
}

function loadDb(): MockDatabase {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return createSeedDatabase()
    const parsed = JSON.parse(raw) as MockDatabase
    if (!parsed || parsed.version !== 2) return createSeedDatabase()
    return parsed
  } catch {
    return createSeedDatabase()
  }
}

function saveDb(db: MockDatabase): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(db))
}

function requireSettings(db: MockDatabase, caretakerId: string): CultureSubsidySettings {
  const settings = db.settings.find(item => item.caretakerId === caretakerId)
  if (!settings) throw new Error('Subvention introuvable pour ce caretaker')
  return settings
}

function requirePockets(db: MockDatabase, caretakerId: string): PocketBalances {
  const pockets = db.pockets[caretakerId]
  if (!pockets) throw new Error('Soldes de poches introuvables')
  return pockets
}

function snapshotFor(db: MockDatabase, caretakerId: string): CultureSubsidySnapshot {
  const settings = requireSettings(db, caretakerId)
  return {
    settings: { ...settings },
    pockets: { ...requirePockets(db, caretakerId) },
    products: db.products.map(product => ({ ...product })),
    operations: db.operations
      .filter(operation => operation.caretakerId === caretakerId)
      .map(operation => ({ ...operation })),
    movements: db.movements
      .filter(movement => movement.caretakerId === caretakerId)
      .map(movement => ({ ...movement })),
    beneficiaries: db.beneficiaries
      .filter(row => row.caretakerId === caretakerId)
      .map(row => ({ ...row })),
    alerts: buildRuntimeAlerts(db).filter(alert => !alert.caretakerId || alert.caretakerId === caretakerId)
  }
}

function addMovement(
  db: MockDatabase,
  caretakerId: string,
  type: ReserveMovement['type'],
  amount: number,
  pocket: PocketKind,
  comment: string
): void {
  db.movements.unshift({
    id: uid('mov'),
    caretakerId,
    type,
    amount,
    pocket,
    orderRef: null,
    beneficiaryName: null,
    date: new Date().toISOString(),
    comment
  })
}

function creditReserve(settings: CultureSubsidySettings, pockets: PocketBalances, amount: number): void {
  pockets.cultureReserve = roundMoney(pockets.cultureReserve + amount)
  settings.currentReserve = pockets.cultureReserve
  settings.updatedAt = new Date().toISOString()
  if (settings.status === 'blocked' && pockets.cultureReserve > 0) {
    settings.status = 'active'
    settings.blockedReason = null
  }
}

function assertPositiveAmount(amount: number): void {
  if (!amount || amount <= 0) throw new Error('Le montant doit être supérieur à 0')
}

function transferFromPocket(
  db: MockDatabase,
  caretakerId: string,
  source: 'free' | 'loyalty' | 'recycling',
  payload: TransferPayload
): CultureSubsidySnapshot {
  const settings = requireSettings(db, caretakerId)
  const pockets = requirePockets(db, caretakerId)
  assertPositiveAmount(payload.amount)
  const available = pockets[source]
  if (payload.amount > available) throw new Error('Solde insuffisant sur la poche source')

  pockets[source] = roundMoney(pockets[source] - payload.amount)
  creditReserve(settings, pockets, payload.amount)

  const type = source === 'free' ? 'feed_from_free' : source === 'loyalty' ? 'feed_from_loyalty' : 'feed_from_recycling'
  addMovement(db, caretakerId, type, payload.amount, 'culture_reserve', payload.comment || defaultComment(type))
  saveDb(db)
  return snapshotFor(db, caretakerId)
}

function defaultComment(type: ReserveMovement['type']): string {
  if (type === 'feed_from_free') return 'Transfert manuel depuis l’acompte libre'
  if (type === 'feed_from_loyalty') return 'Transfert manuel depuis la fidélité'
  if (type === 'feed_from_recycling') return 'Transfert manuel depuis le recyclage'
  if (type === 'feed_wire') return 'Virement direct'
  if (type === 'empty_to_free') return 'Vidage vers l’acompte libre'
  return ''
}

export const mockCultureSubsidyApi: CultureSubsidyApi = {
  async getCaretakerSnapshot(caretakerId?: string): Promise<CultureSubsidySnapshot> {
    await delay()
    const db = loadDb()
    return snapshotFor(db, caretakerId || db.currentCaretakerId)
  },

  async updateSettings(payload: SettingsPayload, caretakerId?: string): Promise<CultureSubsidySnapshot> {
    await delay()
    const db = loadDb()
    const id = caretakerId || db.currentCaretakerId
    const settings = requireSettings(db, id)
    Object.assign(settings, payload, { updatedAt: new Date().toISOString() })
    if (settings.status !== 'blocked') settings.blockedReason = null
    saveDb(db)
    return snapshotFor(db, id)
  },

  async transferFromFree(payload: TransferPayload, caretakerId?: string): Promise<CultureSubsidySnapshot> {
    await delay()
    const db = loadDb()
    return transferFromPocket(db, caretakerId || db.currentCaretakerId, 'free', payload)
  },

  async transferFromLoyalty(payload: TransferPayload, caretakerId?: string): Promise<CultureSubsidySnapshot> {
    await delay()
    const db = loadDb()
    return transferFromPocket(db, caretakerId || db.currentCaretakerId, 'loyalty', payload)
  },

  async transferFromRecycling(payload: TransferPayload, caretakerId?: string): Promise<CultureSubsidySnapshot> {
    await delay()
    const db = loadDb()
    return transferFromPocket(db, caretakerId || db.currentCaretakerId, 'recycling', payload)
  },

  async registerWire(payload: TransferPayload, caretakerId?: string): Promise<CultureSubsidySnapshot> {
    await delay()
    const db = loadDb()
    const id = caretakerId || db.currentCaretakerId
    const settings = requireSettings(db, id)
    const pockets = requirePockets(db, id)
    assertPositiveAmount(payload.amount)
    creditReserve(settings, pockets, payload.amount)
    addMovement(
      db,
      id,
      'feed_wire',
      payload.amount,
      'culture_reserve',
      payload.comment || `Virement ${settings.wireReference}`
    )
    saveDb(db)
    return snapshotFor(db, id)
  },

  async emptyReserve(payload: TransferPayload, caretakerId?: string): Promise<CultureSubsidySnapshot> {
    await delay()
    const db = loadDb()
    const id = caretakerId || db.currentCaretakerId
    const settings = requireSettings(db, id)
    const pockets = requirePockets(db, id)
    assertPositiveAmount(payload.amount)
    if (payload.amount > pockets.cultureReserve) throw new Error('La réserve ne couvre pas ce montant')
    pockets.cultureReserve = roundMoney(pockets.cultureReserve - payload.amount)
    pockets.free = roundMoney(pockets.free + payload.amount)
    settings.currentReserve = pockets.cultureReserve
    settings.updatedAt = new Date().toISOString()
    addMovement(db, id, 'empty_to_free', -payload.amount, 'free', payload.comment || 'Vidage vers l’acompte libre')
    saveDb(db)
    return snapshotFor(db, id)
  },

  async simulatePurchase(payload: SimulateInput, caretakerId?: string): Promise<SubsidyComputation> {
    await delay(120)
    const db = loadDb()
    const id = caretakerId || db.currentCaretakerId
    const settings = requireSettings(db, id)
    const product = db.products.find(item => item.id === payload.productId)
    if (!product) throw new Error('Produit introuvable')
    const beneficiary = payload.beneficiaryId
      ? db.beneficiaries.find(item => item.id === payload.beneficiaryId)
      : null

    return computeSubsidy({
      product,
      settings,
      operations: db.operations.filter(operation => operation.caretakerId === id),
      pockets: requirePockets(db, id),
      orderAmount: payload.orderAmount,
      beneficiaryConsumed: beneficiary ? beneficiary.consumed : 0
    })
  },

  async updateProduct(
    productId: string,
    patch: Partial<Pick<CatalogProduct, 'eligible' | 'valuePerProduct'>>
  ): Promise<CatalogProduct[]> {
    await delay()
    const db = loadDb()
    const product = db.products.find(item => item.id === productId)
    if (!product) throw new Error('Produit introuvable')
    Object.assign(product, patch)
    saveDb(db)
    return db.products.map(item => ({ ...item }))
  },

  async createOperation(payload: OperationPayload, caretakerId?: string): Promise<SpecialOperation> {
    await delay()
    const db = loadDb()
    const id = caretakerId || db.currentCaretakerId
    if (payload.endDate < payload.startDate) throw new Error('La date de fin doit être postérieure au début')

    const product = payload.productId ? db.products.find(item => item.id === payload.productId) : null
    const operation: SpecialOperation = {
      id: uid('op'),
      caretakerId: id,
      type: payload.type,
      productId: payload.productId,
      productName: product ? product.name : null,
      startDate: payload.startDate,
      endDate: payload.endDate,
      boostedRate: payload.boostedRate,
      boostedValue: payload.boostedValue,
      name: payload.name
    }

    const nextOps = [...db.operations.filter(item => item.caretakerId === id), operation]
    const forbidden = findOperationConflicts(nextOps).find(conflict => conflict.kind === 'forbidden')
    if (forbidden) {
      throw new Error(
        forbidden.messageKey === 'cultureSubsidy.conflicts.twoGlobals'
          ? 'Deux opérations globales ne peuvent pas se chevaucher.'
          : 'Deux opérations spéciales ne peuvent pas se chevaucher sur le même produit.'
      )
    }

    db.operations.push(operation)
    saveDb(db)
    return { ...operation }
  },

  async deleteOperation(operationId: string): Promise<void> {
    await delay()
    const db = loadDb()
    db.operations = db.operations.filter(operation => operation.id !== operationId)
    saveDb(db)
  },

  async listSupervisedCaretakers(): Promise<SupervisedCaretaker[]> {
    await delay()
    const db = loadDb()
    return db.settings.map(settings => toSupervisedCaretaker(db, settings))
  },

  async listAlerts(): Promise<SubsidyAlert[]> {
    await delay(80)
    return buildRuntimeAlerts(loadDb())
  },

  async resetDemo(): Promise<void> {
    await delay(80)
    localStorage.removeItem(STORAGE_KEY)
    saveDb(createSeedDatabase())
  }
}

